<template>
  <PageHeader
    breadcrumb
    :items="[{ label: $t('ui.entities.setting', 2), to: '/settings' }, { label: $t('ui.entities.apiKey', 2) }]"
  >
    <template #right>
      <Button
        v-if="$can('manage_api_keys')"
        :label="$t('ui.crud.new', { item: $t('ui.entities.apiKey') })"
        severity="primary"
        icon="plus"
        @click="openCreateModal"
      />
    </template>
  </PageHeader>

  <Card>
    <DataTable
      v-model:filters="filters"
      :value="apiKeys"
      removable-sort
      sort-field="id"
      :rows="20"
      data-key="id"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-settings-admin-api-keys"
      state-storage="session"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
      :global-filter-fields="['id', 'serviceName', 'apiKeyPreview', 'dealershipNames', 'permissionNames']"
      :loading="apiKeysRequest.isLoading.value"
    >
      <template #empty>No records found</template>
      <template #header>
        <div class="flex justify-between">
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="globalFilter"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters"
            />
          </span>
        </div>
      </template>

      <Column
        field="id"
        header="ID"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.id }}
        </template>
      </Column>

      <Column
        field="serviceName"
        header="Name"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.serviceName }}
        </template>
      </Column>

      <Column
        field="apiKeyPreview"
        header="Key"
      >
        <template #body="{ data }">
          <span class="font-mono text-sm">{{ data.apiKeyPreview }}</span>
        </template>
      </Column>

      <Column
        field="dealerships"
        header="Dealerships"
      >
        <template #body="{ data }">
          <ChipList
            v-if="data.dealerships.length"
            :items="data.dealerships.map(dealership => ({ label: dealership.name, size: 'sm' }))"
            :overflow-chip-props="{ size: 'sm' }"
          />
          <div v-else>-</div>
        </template>
      </Column>

      <Column
        field="permissions"
        header="Permissions"
      >
        <template #body="{ data }">
          <ChipList
            v-if="data.permissions.length"
            :items="data.permissions.map(permission => ({ label: permission.name, size: 'sm' }))"
            :overflow-chip-props="{ size: 'sm' }"
          />
          <div v-else>-</div>
        </template>
      </Column>

      <Column
        field="isActive"
        header="Status"
        :sortable="true"
      >
        <template #body="{ data }">
          <ToggleSwitch
            v-model="data.isActive"
            :disabled="statusUpdateIds.includes(data.id)"
            @change="updateStatus(data, $event)"
          />
        </template>
      </Column>

      <Column
        field="createdAt"
        header="Created"
        :sortable="true"
      >
        <template #body="{ data }">
          <span
            v-if="data.createdAt"
            v-tippy="dayjs(data.createdAt).format('lll')"
          >
            {{ dayjs(data.createdAt).from(dayjs()) }}
          </span>
          <span v-else>-</span>
        </template>
      </Column>

      <Column header="Actions">
        <template #body="{ data }">
          <div class="flex">
            <Button
              v-if="$can('manage_api_keys')"
              v-tippy="$t('ui.actions.edit')"
              plain
              icon="pencil"
              severity="secondary"
              @click="openEditModal(data)"
            />
            <Button
              v-if="$can('manage_api_keys')"
              v-tippy="$t('ui.actions.delete')"
              plain
              icon="trash"
              severity="danger"
              :is-loading="deleteRequest.isLoading.value && deletingId === data.id"
              @click="confirmDelete(data)"
            />
          </div>
        </template>
      </Column>
    </DataTable>
  </Card>

  <teleport to="#modals-container">
    <Modal
      :is-open="formModalIsOpen"
      :title="isNewKey ? $t('ui.crud.new', { item: $t('ui.entities.apiKey') }) : $t('ui.crud.update', { item: $t('ui.entities.apiKey') })"
      icon="key"
      :buttons="['ok', 'close']"
      :has-unsaved-changes="hasUnsavedChanges"
      close-text="Cancel"
      :ok-text="isNewKey ? $t('ui.actions.create') : $t('ui.actions.save')"
      :show-loader-on-confirm="true"
      :is-confirming="isSubmitting"
      :disable-close="isSubmitting"
      min-width="720"
      overflow-behaviour="visible"
      @close-modal="closeFormModal"
      @ok-modal="submitForm"
    >
      <FormGroup
        :label="$t('ui.common.name')"
        :is-required="v$.serviceName.required?.$invalid"
      >
        <Input
          v-model="formState.serviceName"
          :has-error="v$.serviceName.$error"
        />
        <InputError :has-error="v$.serviceName.$error">{{ v$.serviceName.$errors[0]?.$message }}</InputError>
      </FormGroup>

      <FormGroup :label="$t('ui.entities.dealership', 2)">
        <Select
          v-model="formState.dealershipIds"
          :options="dealershipsAllowed"
          value-key="id"
          label-key="name"
          mode="multiple"
          chips
          :chips-displayed="10"
          auto-expand
          searchable
          placeholder="Select dealerships"
        />
      </FormGroup>

      <FormGroup :label="$t('ui.common.permission', 2)">
        <Select
          v-model="formState.permissionIds"
          :options="permissions"
          value-key="id"
          label-key="name"
          mode="multiple"
          chips
          :chips-displayed="10"
          auto-expand
          searchable
          placeholder="Select permissions"
        />
      </FormGroup>

      <Alert
        v-if="isNewKey"
        title="The full API key is only shown once after creation"
        message="Copy and store it somewhere safe. You will not be able to see it again."
        icon="lock"
        severity="info"
        :show-close-button="false"
        :show-dismiss-button="false"
      />
    </Modal>

    <Modal
      :is-open="secretModalIsOpen"
      title="API key created"
      description="Copy this key now. It will not be shown again."
      icon="key"
      :buttons="['close']"
      min-width="520"
      @close-modal="closeSecretModal"
    >
      <Alert
        title="Keep this key private"
        message="Anyone with this key can act with the permissions and dealership access you assigned."
        icon="triangle-exclamation"
        severity="warning"
        :show-close-button="false"
        :show-dismiss-button="false"
      />
      <div class="mt-4 flex items-center gap-2 rounded-lg border border-secondary bg-secondary p-4">
        <span class="font-mono text-sm break-all">{{ createdSecret }}</span>
        <CopyText
          v-if="createdSecret"
          :explicit-text-to-copy="createdSecret"
          show-icon
          class="text-tertiary"
        />
      </div>
    </Modal>
  </teleport>
</template>

<script setup lang="ts">
// composables
import { computed, onMounted, ref, watch } from 'vue'
import useApiRequest from '@/composables/useApiRequest'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
// components
import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import Button from '@/components/button/Button.vue'
import Input from '@/components/forms/Input.vue'
import InputError from '@/components/forms/InputError.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import Select from '@/components/forms/Select.vue'
import ToggleSwitch from '@/components/forms/ToggleSwitch.vue'
import Modal from '@/components/modal/Modal.vue'
import Alert from '@/components/alert/Alert.vue'
import ChipList from '@/components/chip/ChipList.vue'
import CopyText from '@/components/CopyText.vue'
// stores
import { useAuthStore } from '@/stores/AuthStore'
// types
import type { ApiKey, ApiKeyPermission, RolesCatalogue } from '@/types/domain/apiKey'
// utilities
import { alertToast } from '@/utilities/notification'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { FilterMatchMode } from '@primevue/core/api'
import dayjs from 'dayjs'
import Swal from 'sweetalert2'

interface ApiKeyRow extends ApiKey {
  dealershipNames: string
  permissionNames: string
}

interface ApiKeyFormState {
  id: number | null
  serviceName: string
  permissionIds: number[]
  dealershipIds: number[]
}

interface FilterValue {
  value: string | number[] | undefined
  matchMode: string
}

/**
 * Empty form used when creating a key or resetting the modal.
 */
const defaultFormState = (): ApiKeyFormState => ({
  id: null,
  serviceName: '',
  permissionIds: [],
  dealershipIds: []
})

const authStore = useAuthStore()
const apiKeys = ref<ApiKeyRow[]>([])
const permissions = ref<ApiKeyPermission[]>([])
const statusUpdateIds = ref<number[]>([])
const deletingId = ref<number | null>(null)
const formModalIsOpen = ref(false)
const secretModalIsOpen = ref(false)
const isNewKey = ref(false)
const hasUnsavedChanges = ref(false)
const createdSecret = ref('')
const formState = ref<ApiKeyFormState>(defaultFormState())
const formStateInit = ref<ApiKeyFormState>(defaultFormState())
const filters = ref<Record<string, FilterValue>>({})

const apiKeysRequest = useApiRequest()
const rolesRequest = useApiRequest()
const saveRequest = useApiRequest()
const deleteRequest = useApiRequest()

/** Dealerships the current user can assign, including every site for admins. */
const dealershipsAllowed = computed(() => authStore.accessAllowed.dealerships)

const isSubmitting = computed(() => saveRequest.isLoading.value)

/**
 * Bridge PrimeVue's flexible filter value to the string-only Input model.
 */
const globalFilter = computed<string | undefined>({
  get: () => {
    const value = filters.value.global?.value

    return typeof value === 'string' ? value : undefined
  },
  set: value => {
    filters.value.global.value = value
  }
})

const v$ = useVuelidate(
  {
    serviceName: { required }
  },
  formState
)

/**
 * Initialize table filters.
 */
function initFilters() {
  filters.value = {
    global: { value: undefined, matchMode: FilterMatchMode.CONTAINS }
  }
}
initFilters()

/**
 * Clear all table filters.
 */
function clearFilters() {
  initFilters()
}

/**
 * Map an API key record into a searchable table row.
 *
 * @param {ApiKey} apiKey - API key from the list/read/create response.
 */
function toApiKeyRow(apiKey: ApiKey): ApiKeyRow {
  return {
    id: apiKey.id,
    serviceName: apiKey.serviceName,
    apiKeyPreview: apiKey.apiKeyPreview,
    isActive: apiKey.isActive,
    createdAt: apiKey.createdAt,
    permissions: apiKey.permissions,
    dealerships: apiKey.dealerships,
    dealershipNames: apiKey.dealerships.map(dealership => dealership.name).join(', '),
    permissionNames: apiKey.permissions.map(permission => permission.name).join(', ')
  }
}

/**
 * Replace or insert a row in the in-memory table after a successful save.
 *
 * @param {ApiKey} apiKey - Updated or created API key.
 */
function upsertApiKey(apiKey: ApiKey) {
  const row = toApiKeyRow(apiKey)
  const index = apiKeys.value.findIndex(existing => existing.id === apiKey.id)

  // Replace the existing row when editing; prepend when creating.
  if (index >= 0) {
    apiKeys.value[index] = row
    return
  }

  apiKeys.value = [row, ...apiKeys.value]
}

/**
 * Load API keys into the table.
 */
async function loadApiKeys() {
  try {
    const response = await apiKeysRequest.send<ApiKey[]>({
      endpoint: '/v1/api-keys'
    })

    // The list endpoint returns the keys in the normalized data payload.
    apiKeys.value = Array.isArray(response.data) ? response.data.map(toApiKeyRow) : []
  } catch {
    apiKeys.value = []
  }
}

/**
 * Load the full permission catalogue from /v1/roles (users/roles).
 */
async function loadPermissions() {
  try {
    const response = await rolesRequest.send<RolesCatalogue>({
      endpoint: '/v1/roles'
    })

    // Permissions live alongside roles in this catalogue endpoint.
    permissions.value = Array.isArray(response.data?.permissions) ? response.data.permissions : []
  } catch {
    permissions.value = []
  }
}

/**
 * Open the modal to create a new API key.
 */
function openCreateModal() {
  isNewKey.value = true
  formState.value = defaultFormState()
  formStateInit.value = defaultFormState()
  v$.value.$reset()
  formModalIsOpen.value = true
}

/**
 * Open the modal to edit an existing API key.
 *
 * @param {ApiKeyRow} apiKey - Table row being edited.
 */
function openEditModal(apiKey: ApiKeyRow) {
  isNewKey.value = false
  const nextState: ApiKeyFormState = {
    id: apiKey.id,
    serviceName: apiKey.serviceName,
    permissionIds: apiKey.permissions.map(permission => permission.id),
    dealershipIds: apiKey.dealerships.map(dealership => dealership.id)
  }
  formState.value = {
    ...nextState,
    permissionIds: [...nextState.permissionIds],
    dealershipIds: [...nextState.dealershipIds]
  }
  formStateInit.value = {
    ...nextState,
    permissionIds: [...nextState.permissionIds],
    dealershipIds: [...nextState.dealershipIds]
  }
  v$.value.$reset()
  formModalIsOpen.value = true
}

/**
 * Close the create/edit modal and discard unsaved form state.
 */
function closeFormModal() {
  formModalIsOpen.value = false
  hasUnsavedChanges.value = false
  formState.value = defaultFormState()
  formStateInit.value = defaultFormState()
  v$.value.$reset()
}

/**
 * Close the one-time secret modal.
 */
function closeSecretModal() {
  secretModalIsOpen.value = false
  createdSecret.value = ''
}

/**
 * Create or update an API key from the modal form.
 */
async function submitForm() {
  const isFormCorrect = await v$.value.$validate()
  // Block submit until the name is present.
  if (!isFormCorrect) {
    return
  }

  try {
    const payload = {
      serviceName: formState.value.serviceName,
      permissions: formState.value.permissionIds,
      dealerships: formState.value.dealershipIds
    }

    const response = isNewKey.value
      ? await saveRequest.send<ApiKey>({
          method: 'POST',
          endpoint: '/v1/api-keys',
          data: payload
        })
      : await saveRequest.send<ApiKey>({
          method: 'PATCH',
          endpoint: `/v1/api-keys/${formState.value.id}`,
          data: payload
        })

    if (!response.success || !response.data) {
      alertToast(isNewKey.value ? 'API key not created' : 'API key not updated', response.message, 'error')
      return
    }

    const wasCreate = isNewKey.value
    const secret = response.data.apiKey
    upsertApiKey(response.data)
    closeFormModal()

    // The full secret is only returned on create.
    if (wasCreate && secret) {
      createdSecret.value = secret
      secretModalIsOpen.value = true
      alertToast('API key created', 'Copy the key now. It will not be shown again.', 'success')
      return
    }

    alertToast(wasCreate ? 'API key created' : 'API key updated', null, 'success')
  } catch (err) {
    alertToast(isNewKey.value ? 'API key not created' : 'API key not updated', (err as Error).message, 'error')
  }
}

/**
 * Toggle an API key's active state from the table.
 *
 * @param {ApiKeyRow} apiKey - Row being updated.
 * @param {boolean} nextStatus - New active state from the switch.
 */
async function updateStatus(apiKey: ApiKeyRow, nextStatus: boolean) {
  const previousStatus = !nextStatus
  statusUpdateIds.value = [...statusUpdateIds.value, apiKey.id]

  try {
    const response = await useApiRequest().send<ApiKey>({
      endpoint: `/v1/api-keys/${apiKey.id}`,
      method: 'PATCH',
      data: {
        isActive: nextStatus
      }
    })

    // Revert the optimistic toggle when the API rejects the update.
    if (!response.success || !response.data) {
      apiKey.isActive = previousStatus
      alertToast('API key status not updated', response.message, 'error')
      return
    }

    upsertApiKey(response.data)
    alertToast('API key status updated', null, 'success')
  } catch {
    apiKey.isActive = previousStatus
  } finally {
    statusUpdateIds.value = statusUpdateIds.value.filter(id => id !== apiKey.id)
  }
}

/**
 * Confirm and delete an API key.
 *
 * @param {ApiKeyRow} apiKey - Row to delete.
 */
function confirmDelete(apiKey: ApiKeyRow) {
  Swal.fire({
    title: 'Are you sure?',
    icon: 'warning',
    reverseButtons: true,
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes, delete this API key',
    confirmButtonColor: 'var(--bg-error-solid)',
    cancelButtonText: 'Cancel'
  }).then(async result => {
    // Only delete after the user confirms.
    if (!result.isConfirmed) {
      return
    }

    deletingId.value = apiKey.id

    try {
      const response = await deleteRequest.send({
        method: 'DELETE',
        endpoint: `/v1/api-keys/${apiKey.id}`
      })

      if (!response.success) {
        alertToast('API key not deleted', response.message, 'error')
        return
      }

      apiKeys.value = apiKeys.value.filter(existing => existing.id !== apiKey.id)
      alertToast('API key deleted', null, 'success')
    } catch (err) {
      alertToast('API key not deleted', (err as Error).message, 'error')
    } finally {
      deletingId.value = null
    }
  })
}

watch(
  formState,
  () => {
    hasUnsavedChanges.value = JSON.stringify(formState.value) !== JSON.stringify(formStateInit.value)
  },
  { deep: true }
)

onMounted(async () => {
  await Promise.all([loadApiKeys(), loadPermissions()])
})
</script>
